/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled from "styled-components";
import checkImage from "./images/check-green.svg";
import crossImage from "./images/cross-gray.svg";
import { devices } from "components/shared/Styles";
import { PremiumIcon } from "components/shared/PremiumIcon";

const Check = styled.img.attrs({
  src: checkImage,
  alt: I18n.t("plans.features.check"),
})`
  width: 18px;
  padding: 0 8px 3px 0;
  vertical-align: middle;
`;

const Cross = styled.img.attrs({
  src: crossImage,
  alt: I18n.t("plans.features.cross"),
})`
  width: 18px;
  padding: 0 8px 3px 0;
  vertical-align: middle;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 900;
`;

const FeaturesTable = styled.table`
  border-collapse: separate;
  width: 100%;
  border-spacing: 0 10px;
  font-size: 16px;

  thead {
    font-size: 22px;
    font-weight: 900;
    text-align: left;
  }

  th {
    padding: 20px 0 6px 0;
    vertical-align: middle;
  }

  tbody tr {
    background-color: var(--spectrum-gray-100);
  }

  td {
    padding: 14px 4px;
    vertical-align: middle;
  }

  td:first-child {
    border-radius: 16px 0 0 16px;
    padding-left: 14px;
    width: 60%;
  }

  td:last-child {
    border-radius: 0 16px 16px 0;
    padding-right: 14px;
  }
`;

const PremiumContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

const PremiumTitle = styled.span`
  display: inline;

  ${devices.small} {
    display: none;
  }
`;

export const FeaturesTableHeader: React.FC<{ featureName: string }> = ({
  featureName,
}) => {
  return (
    <thead>
      <tr>
        <th>{featureName}</th>
        <th>{I18n.t("plans.features.free")}</th>
        <th>
          <PremiumContainer>
            <PremiumTitle>{I18n.t("plans.features.premium")}</PremiumTitle>{" "}
            <PremiumIcon />
          </PremiumContainer>
        </th>
      </tr>
    </thead>
  );
};

export const FeaturesTableRows: React.FC = ({ children, ...props }) => {
  return (
    <tr>
      {React.Children.map(children, (child) => (
        <td style={{ verticalAlign: "top" }}>
          {React.cloneElement(child as JSX.Element, props)}
        </td>
      ))}
    </tr>
  );
};

export const Features: React.FC = () => {
  return (
    <>
      <Title>{I18n.t("plans.features.title")}</Title>
      <FeaturesTable>
        <FeaturesTableHeader
          featureName={I18n.t("plans.features.enhance_speech.title")}
        />
        <tbody>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.enhance_speech.audio.title")}</>
            <Check />
            <Check />
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.enhance_speech.video.title")}</>
            <Cross />
            <Check />
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.enhance_speech.bulk_upload.title")}</>
            <Cross />
            <Check />
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.enhance_speech.strength.title")}</>
            <Cross />
            <Check />
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.enhance_speech.file_size.title")}</>
            <>{I18n.t("plans.features.enhance_speech.file_size.free")}</>
            <>{I18n.t("plans.features.enhance_speech.file_size.premium")}</>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.enhance_speech.duration.title")}</>
            <>{I18n.t("plans.features.enhance_speech.duration.free")}</>
            <>{I18n.t("plans.features.enhance_speech.duration.premium")}</>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.enhance_speech.hours.title")}</>
            <>{I18n.t("plans.features.enhance_speech.hours.free")}</>
            <>{I18n.t("plans.features.enhance_speech.hours.premium")}</>
          </FeaturesTableRows>
        </tbody>
        <FeaturesTableHeader
          featureName={I18n.t("plans.features.podcast_studio.title")}
        />
        <tbody>
          <FeaturesTableRows>
            <>
              {I18n.t("plans.features.podcast_studio.remote_recording.title")}
            </>
            <>{I18n.t("plans.features.podcast_studio.remote_recording.free")}</>
            <>
              {I18n.t("plans.features.podcast_studio.remote_recording.premium")}
            </>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>
              {I18n.t(
                "plans.features.podcast_studio.max_project_duration.title"
              )}
            </>
            <>
              {I18n.t(
                "plans.features.podcast_studio.max_project_duration.free"
              )}
            </>
            <>
              {I18n.t(
                "plans.features.podcast_studio.max_project_duration.premium"
              )}
            </>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.podcast_studio.downloads.title")}</>
            <>
              {I18n.t("plans.features.podcast_studio.downloads.free_projects")}
            </>
            <>{I18n.t("plans.features.podcast_studio.downloads.premium")}</>
          </FeaturesTableRows>

          <FeaturesTableRows>
            <>{I18n.t("plans.features.podcast_studio.transcription.title")}</>
            <>
              <Check />
            </>
            <>
              <Check />
            </>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>
              {I18n.t(
                "plans.features.podcast_studio.transcription_editing.title"
              )}
            </>
            <>
              <Check />
            </>
            <>
              <Check />
            </>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.podcast_studio.enhancement.title")}</>
            <>
              <Cross />
            </>
            <>
              <Check />
            </>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>
              {I18n.t(
                "plans.features.podcast_studio.download_original_recordings.title"
              )}
            </>
            <>
              <Cross />
            </>
            <>
              <Check />
            </>
          </FeaturesTableRows>
          <FeaturesTableRows>
            <>
              {I18n.t(
                "plans.features.podcast_studio.remove_podcast_branding.title"
              )}
            </>
            <>
              <Cross />
            </>
            <>
              <Check />
            </>
          </FeaturesTableRows>
        </tbody>
        <FeaturesTableHeader
          featureName={I18n.t("plans.features.mic_check.title")}
        />
        <tbody>
          <FeaturesTableRows>
            <>{I18n.t("plans.features.mic_check.analyze.title")}</>
            <Check />
            <Check />
          </FeaturesTableRows>
        </tbody>
      </FeaturesTable>
    </>
  );
};
